/**
 * staffSkillModel.ts
 * Pure, dependency-free skill logic for the "Staff this job" v2 view
 * (STAFF-VIEW-V2.md). Turns a person's recorded skill level (1–5 | null) for a
 * task's REQUIRED skill into a "fit" the UI can badge, orders picker candidates
 * QUALIFIED-FIRST then by spare capacity, and phrases the badge label.
 *
 * The task-first reframe puts skill everywhere: every assigned chip carries a
 * fit badge, and the ＋Assign picker lists qualified people first. Mentor
 * detection (L1/L2 with no L3+ present) lives in utils/mentorNudge.ts — this
 * module only classifies a single (level, skillRequired) pair.
 *
 * No React / MUI / theme — the components pick the token colors.
 */

import { MENTOR_SKILL_LEVEL } from '../../../../utils/rosterAllocation';

/**
 * How a person fits a task's required skill:
 *   - 'no-skill'   — the task type requires no skill (gate always passes).
 *   - 'qualified'  — level ≥ L3 (proficient, can work the task alone).
 *   - 'needs-mentor' — level L1/L2 (must have an L3+ mentor present).
 *   - 'unrated'    — the task needs a skill but the person has no level on
 *                    record → a warning (assigned without the required skill).
 */
export type SkillFit = 'no-skill' | 'qualified' | 'needs-mentor' | 'unrated';

/** Classify one person's level against a task's skill requirement. */
export function classifySkill(
  level: number | null | undefined,
  skillRequired: boolean,
): SkillFit {
  if (!skillRequired) return 'no-skill';
  if (typeof level === 'number' && level >= MENTOR_SKILL_LEVEL) return 'qualified';
  if (typeof level === 'number' && level > 0) return 'needs-mentor';
  return 'unrated';
}

/** Sort rank for QUALIFIED-FIRST ordering (lower sorts first). */
export function skillSortRank(fit: SkillFit): number {
  switch (fit) {
    case 'qualified': return 0;
    case 'needs-mentor': return 1;
    case 'unrated': return 2;
    case 'no-skill': return 3;
  }
}

/** Short badge label, e.g. "Qualified · L4" / "Needs mentor · L2". */
export function skillBadgeLabel(fit: SkillFit, level: number | null | undefined): string {
  switch (fit) {
    case 'no-skill': return 'No skill needed';
    case 'qualified': return `Qualified · L${level}`;
    case 'needs-mentor': return `Needs mentor · L${level}`;
    case 'unrated': return 'Not qualified';
  }
}

/**
 * WORDLESS compact label for the redesigned person card (BUG-2026-0141 #5): just
 * the L-number — the color + icon + legend carry the "qualified / needs-mentor /
 * not-qualified / no-skill" meaning, so no "Qualified"/"Needs mentor" words.
 *   - qualified / needs-mentor → "L4" / "L2"
 *   - unrated (skill required, no level on record) → "L?"
 *   - no-skill (task needs none) → "—"
 */
export function skillBadgeCompactLabel(fit: SkillFit, level: number | null | undefined): string {
  switch (fit) {
    case 'no-skill': return '—';
    case 'qualified': return `L${level}`;
    case 'needs-mentor': return `L${level}`;
    case 'unrated': return 'L?';
  }
}

/** A candidate for the skill-smart ＋Assign picker. */
export interface StaffCandidate {
  userKey: string;
  displayName: string;
  /** Skill level (1–5) for the task's required skill, or null when unrated. */
  level: number | null;
  /** Peak weekly utilization % (for the least-loaded tie-break + hint). */
  allocationPercent: number;
}

/**
 * Order candidates for a task's ＋Assign picker: QUALIFIED-FIRST (L3+ before
 * L1/L2 before unrated), then least-loaded first, then name. When the task
 * needs no skill, everyone is 'no-skill' so it collapses to a pure load sort.
 * Returns a NEW array (memo-friendly).
 */
export function sortCandidatesForTask(
  candidates: readonly StaffCandidate[],
  skillRequired: boolean,
): StaffCandidate[] {
  return [...candidates].sort((a, b) => {
    const rankDiff =
      skillSortRank(classifySkill(a.level, skillRequired)) -
      skillSortRank(classifySkill(b.level, skillRequired));
    if (rankDiff !== 0) return rankDiff;
    const loadDiff = (a.allocationPercent || 0) - (b.allocationPercent || 0);
    if (loadDiff !== 0) return loadDiff;
    return a.displayName.localeCompare(b.displayName);
  });
}
